import { useState, useEffect, useCallback } from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { cn } from '../../lib/utils';
import { 
  Eye,
  RefreshCw,
  AlertCircle,
  Ticket,
  CheckCircle2,
  Zap,
  Building2,
  ExternalLink
} from 'lucide-react';
import { Button } from '../ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Company card component
const CompanyCard = ({ company, timeRangeDays }) => {
  const hasError = company.source === 'error';
  const tickets = company.tickets || { open: 0, closed: 0, total: 0 };
  
  return (
    <Card 
      className={cn(
        "p-5 bg-card/80 backdrop-blur-sm border-border/50 transition-all hover:shadow-lg",
        hasError && "opacity-70"
      )}
      data-testid={`company-${company.key}`}
    >
      {/* Company Header */}
      <div className="flex items-start gap-4 mb-4">
        {company.logo ? (
          <div 
            className="w-14 h-14 rounded-xl flex items-center justify-center overflow-hidden bg-white p-1.5 shadow-sm border border-border/30"
          >
            <img 
              src={company.logo} 
              alt={`${company.name} logo`}
              className="w-full h-full object-contain"
            />
          </div>
        ) : (
          <div 
            className="w-14 h-14 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: company.color ? `${company.color}20` : 'rgb(var(--primary) / 0.1)' }}
          >
            <Building2 
              className="w-7 h-7" 
              style={{ color: company.color || 'rgb(var(--primary))' }}
            />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <h4 className="text-lg font-semibold truncate" style={{ fontFamily: 'Chivo, sans-serif' }}>
            {company.name}
          </h4>
          {company.description && (
            <p className="text-xs text-muted-foreground truncate">{company.description}</p>
          )}
          <div className="flex items-center gap-2 mt-1">
            <Badge variant="outline" className="text-xs px-1.5 py-0">
              {company.jira_project}
            </Badge>
            {!hasError && (
              <Badge className="bg-[rgb(var(--success))]/10 text-[rgb(var(--success))] border-[rgb(var(--success))]/20 text-xs px-1.5 py-0">
                <Zap className="w-2.5 h-2.5 mr-0.5" />
                Live
              </Badge>
            )}
          </div>
        </div>
      </div>

      {/* Error State */}
      {hasError ? (
        <div className="text-center py-4 px-2 rounded-lg bg-[rgb(var(--warning))]/5 border border-[rgb(var(--warning))]/20">
          <AlertCircle className="w-6 h-6 mx-auto mb-2 text-[rgb(var(--warning))]" />
          <p className="text-xs text-[rgb(var(--warning))] font-medium">Unable to connect</p>
          <p className="text-xs text-muted-foreground mt-1">{company.error || 'Connection failed'}</p>
        </div>
      ) : (
        /* Ticket Stats */
        <div className="grid grid-cols-3 gap-2">
          <div className="text-center p-3 rounded-lg bg-muted/30 border border-border/30">
            <div className="flex items-center justify-center gap-1 mb-1">
              <Ticket className="w-3 h-3 text-primary" />
              <p className="text-xs text-muted-foreground">Open</p>
            </div>
            <p 
              className="text-2xl font-bold"
              style={{ fontFamily: 'Chivo, sans-serif', color: company.color || 'rgb(var(--primary))' }}
            >
              {tickets.open}
            </p>
          </div>
          <div className="text-center p-3 rounded-lg bg-muted/30 border border-border/30">
            <div className="flex items-center justify-center gap-1 mb-1">
              <CheckCircle2 className="w-3 h-3 text-[rgb(var(--success))]" />
              <p className="text-xs text-muted-foreground">{timeRangeDays}d</p>
            </div>
            <p className="text-2xl font-bold text-[rgb(var(--success))]" style={{ fontFamily: 'Chivo, sans-serif' }}>
              {tickets.closed}
            </p>
          </div>
          <div className="text-center p-3 rounded-lg bg-muted/30 border border-border/30">
            <div className="flex items-center justify-center gap-1 mb-1">
              <ExternalLink className="w-3 h-3 text-muted-foreground" />
              <p className="text-xs text-muted-foreground">Total</p>
            </div>
            <p className="text-2xl font-bold text-muted-foreground" style={{ fontFamily: 'Chivo, sans-serif' }}>
              {tickets.total}
            </p>
          </div>
        </div>
      )}
    </Card>
  );
};

export const EverWatchSection = ({ timeRange = '7 days', className }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Convert time range to days
  const getTimeRangeDays = useCallback(() => {
    switch (timeRange) {
      case '7 days': return 7;
      case '30 days': return 30;
      case '90 days': return 90;
      default: return 7;
    }
  }, [timeRange]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const days = getTimeRangeDays();
      const response = await fetch(`${API_URL}/api/everwatch?time_range_days=${days}`);
      const result = await response.json();
      
      if (result.error && result.source === 'error' && !result.companies?.length) {
        setError(result.error);
        setData(null);
      } else {
        setData(result);
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch EverWatch data');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [getTimeRangeDays]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const companies = data?.companies || [];
  const totals = data?.totals || { open: 0, closed: 0, company_count: 0 };
  const timeRangeDays = data?.time_range_days || 7;
  const hasLiveData = data?.source === 'jira_live' || companies.some(c => c.source === 'jira_live');

  return (
    <div className={cn('space-y-6', className)} data-testid="everwatch-section">
      {/* Section Header Card */}
      <Card className="p-6 bg-card/80 backdrop-blur-sm border-border/50">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <Eye className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h3 className="text-lg font-semibold" style={{ fontFamily: 'Chivo, sans-serif' }}>
                EverWatch Technologies
              </h3>
              <p className="text-xs text-muted-foreground">
                Managed IT Services & Support Contracts
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {hasLiveData && (
              <Badge className="bg-[rgb(var(--success))]/10 text-[rgb(var(--success))] border-[rgb(var(--success))]/20 gap-1">
                <Zap className="w-3 h-3" />
                Live ({timeRangeDays}d)
              </Badge>
            )}
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={fetchData}
              disabled={loading}
              data-testid="refresh-everwatch"
            >
              <RefreshCw className={cn("w-4 h-4", loading && "animate-spin")} />
            </Button>
          </div>
        </div>
        
        {/* Summary Stats */}
        {!loading && !error && companies.length > 0 && (
          <div className="grid grid-cols-3 gap-4">
            <div className="p-4 rounded-xl bg-muted/30 border border-border/50 text-center">
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Active Contracts</p>
              <p className="text-3xl font-bold text-primary" style={{ fontFamily: 'Chivo, sans-serif' }}>
                {totals.company_count}
              </p>
            </div>
            <div className="p-4 rounded-xl bg-muted/30 border border-border/50 text-center">
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Total Open</p>
              <p className="text-3xl font-bold text-primary" style={{ fontFamily: 'Chivo, sans-serif' }}>
                {totals.open}
              </p>
            </div>
            <div className="p-4 rounded-xl bg-muted/30 border border-border/50 text-center">
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Closed ({timeRangeDays}d)</p>
              <p className="text-3xl font-bold text-[rgb(var(--success))]" style={{ fontFamily: 'Chivo, sans-serif' }}>
                {totals.closed}
              </p>
            </div>
          </div>
        )}
      </Card>

      {/* Loading State */}
      {loading && !data ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
          <span className="ml-3 text-muted-foreground">Loading company data...</span>
        </div>
      ) : error && !companies.length ? (
        /* Error State */
        <Card className="p-8 bg-card/80 backdrop-blur-sm border-border/50">
          <div className="text-center">
            <AlertCircle className="w-12 h-12 mx-auto mb-4 text-[rgb(var(--warning))] opacity-50" />
            <p className="font-semibold text-[rgb(var(--warning))]">Unable to Load Companies</p>
            <p className="text-sm text-muted-foreground mt-2">
              {error || 'Unable to connect to Jira for EverWatch projects'}
            </p>
          </div>
        </Card>
      ) : companies.length === 0 ? (
        /* Empty State */
        <Card className="p-8 bg-card/80 backdrop-blur-sm border-border/50">
          <div className="text-center">
            <Building2 className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
            <p className="font-semibold text-muted-foreground">No Active Contracts</p>
            <p className="text-sm text-muted-foreground mt-2">
              Configure company contracts in backend/server.py EVERWATCH_COMPANIES
            </p>
          </div>
        </Card>
      ) : (
        /* Company Cards Grid */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {companies.map((company) => (
            <CompanyCard 
              key={company.key} 
              company={company} 
              timeRangeDays={timeRangeDays}
            />
          ))}
        </div>
      )}
    </div>
  );
};
